import $ from 'jquery';
import { LayoutUpdateTracker } from '../../../FrontendBundle/Resources/js/ofcs';
import Editor from './editor';
import GuideManager from './editor/canvas/guideManager';
import SelectionCollection from './editor/canvas/selectionCollection';
import ViewCollection from './editor/canvas/viewCollection';
import Clipboard from './editor/clipboard';
import AdapterFactory from './editor/clipboard/adapterFactory';
import CollisionExceptionService from './editor/collision/collisionExceptionService';
import ItemCollisionChecker from './editor/collision/itemCollisionChecker';
import ItemOverlappingCollisionChecker from './editor/collision/itemOverlappingCollisionChecker';
import OutOfContainerChecker from './editor/collision/outOfContainerChecker';
import PolygonCollisionChecker from './editor/collision/polygonCollisionChecker';
import TabletopCollisionChecker from './editor/collision/tabletopCollisionChecker';
import TreeCollisionChecker from './editor/collision/treeCollisionChecker';
import CommandExecutor from './editor/command/commandExecutor';
import CommandFactory from './editor/command/factory';
import History from './editor/command/history';
import ContextManager from './editor/context/contextManager';
import ContextVisualizer from './editor/context/contextVisualizer';
import EditorKeyBinder from './editor/editorKeyBinder';
import GridManager from './editor/grid/gridManager';
import ChainConfigBuilder from './editor/item/configBuilder/chainConfigBuilder';
import EditorItemFilter from './editor/item/editorItemFilter';
import ProductFamilyStore from './editor/item/productFamilyStore';
import ProductionDataStore from './editor/item/productionDataStore';
import ProjectListItemStore from './editor/item/projectListItemStore';
import ItemEnvironmentFactory from './editor/itemEnvironment/itemEnvironmentFactory';
import LayoutFactory from './editor/layoutFactory';
import SvgCanvas from './editor/svg/canvas';
import LayerManager from './editor/svg/layer/layerManager';
import PathFactory from './editor/svg/pathFactory';
import InsideTabletopManager from './editor/tabletop/insideTabletopManager';
import ChainValidator from './editor/validator/chainValidator';
import CollisionValidator from './editor/validator/collisionValidator';
import ItemAvailableValidator from './editor/validator/itemAvailableValidator';
import DelegatingStrategy from './editor/validator/itemHeightStrategy/delegatingStrategy';
import FullyRecessedStrategy from './editor/validator/itemHeightStrategy/fullyRecessedStrategy';
import ItemDepthStrategy from './editor/validator/itemHeightStrategy/itemDepthStrategy';
import ItemHeightValidator from './editor/validator/itemHeightValidator';
import ItemSuccessorValidator from './editor/validator/itemSuccessorValidator';
import ItemVersionValidator from './editor/validator/itemVersionValidator';
import OutOfContainerValidator from './editor/validator/outOfContainerValidator';
import ProblemFactory from './editor/validator/problem/problemFactory';
import TabletopAreaValidator from './editor/validator/tabletopAreaValidator';
import TabletopCollisionValidator from './editor/validator/tabletopCollisionValidator';
import ValidationWatcher from './editor/validator/validationWatcher';
import BadgeView from './editor/validator/view/badgeView';
import CollisionView from './editor/validator/view/collisionView';
import ValidatorView from './editor/validator/view/validatorView';

/**
 * Function which adds services to the container
 * @param {Pimple} container
 */
export default function buildContainer(container) {
    container.share('editor.itemEnvironmentFactory', c => new ItemEnvironmentFactory(c));
    container.share('editor.layoutFactory', c => new LayoutFactory(c));
    container.share('editor.layoutUpdateTracker', () => new LayoutUpdateTracker());
    container.set('editor.name', 'default');
    container.set('editor.deletableItemFilter', new EditorItemFilter());

    container.share('editor', c => (
        new Editor(
            c.get('editor.layoutFactory'),
            c.get('canvas.itemFactory'),
            c.get('canvas.validator'),
            c.get('editor.layoutUpdateTracker'),
            c.get('editor.translations'),
            c.get('canvas.contextManager'),
            {
                editorUrl: c.get('editor.url.editorUrl'),
                saveUrl: c.get('editor.url.saveUrl'),
                loadingIndicatorTemplate: c.get('editor.template.loadingIndicator'),
                loadingIndicatorMessage: c.get('editor.message.loadingLayout'),
                name: c.get('editor.name'),
            },
        )
    ));

    container.share('editor.keybinder', c => (
        new EditorKeyBinder(
            c.get('editor.el.keybinder'),
            c.get('editor'),
            c.get('editor.clipboard'),
        )
    ));

    container.share('editor.clipboard', (c) => {
        const factory = new AdapterFactory(window);

        return new Clipboard(
            factory.create(),
            c.get('canvas.itemFactory'),
            c.get('canvas.selection'),
            c.get('canvas.contextManager'),
            c.get('canvas.command.history'),
            c.get('canvas'),
            c.get('editor.deletableItemFilter'),
        );
    });

    container.share('canvas.svg.element', c => $(c.get('canvas.wrapper')).svg());
    container.share('canvas.svg.wrapper', (c) => {
        const element = c.get('canvas.svg.element');
        const wrapper = element.svg('get');
        const root = $(wrapper.root());

        root.attr('xmlns', 'http://www.w3.org/2000/svg');
        root.attr('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        root.css('background-color', c.get('editor.color.backgroundColor'));

        return wrapper;
    });
    container.share('canvas.svg.pathFactory', c => new PathFactory(c.get('canvas.svg.wrapper')));
    container.share('canvas.svg.layerManager', c => new LayerManager(
        c.get('canvas.svg.wrapper'),
    ));
    container.share('canvas.command.history', c => new History(
        c.get('canvas.command.commandExecutor'),
    ));
    container.share('canvas.command.commandExecutor', () => new CommandExecutor());
    container.share('canvas.command.factory', c => new CommandFactory(c.get('canvas.selection')));
    container.share('canvas.contextManager', () => new ContextManager());

    container.share('canvas.contextVisualizer', c => new ContextVisualizer(
        c.get('canvas.contextManager'),
        c.get('canvas.svg.layerManager'),
    ));

    container.set('canvas.guideManager.isActive', false);

    container.share('canvas.guideManager', c => new GuideManager(
        c.get('canvas.tools.select'),
        c.get('canvas.contextManager'),
        c.get('editor.value.snapTolerance'),
        c.get('canvas.guideManager.isActive'),
    ));

    container.share('canvas.grid.gridManager', c => new GridManager(
        c.get('canvas.tools.select'),
        c.get('canvas.contextManager'),
        c.get('canvas.command.commandExecutor'),
    ));

    container.share('canvas.insideTabletopManager', c => new InsideTabletopManager(
        c.get('canvas.command.commandExecutor'),
    ));

    container.share('canvas.gridManager', () => new Ofcs.Editor.Canvas.GridManager());
    container.share('canvas.productFamilyStore', () => new ProductFamilyStore());
    container.share('canvas.item.foamConnector', c => new Ofcs.Editor.Item.Connector.FoamConnector(
        c.get('editor.url.foamItemUrl'),
    ));
    container.share('canvas.item.gridConnector', c => new Ofcs.Editor.Item.Connector.GridConnector(
        c.get('editor.url.gridItemUrl'),
    ));
    container.share('canvas.item.connector', c => new Ofcs.Editor.Item.Connector.DelegatingConnector({
        foam: c.get('canvas.item.foamConnector'),
        rackboxgrid: c.get('canvas.item.gridConnector'),
    }));
    container.share('canvas.productionDataStore', c => new ProductionDataStore(
        c.get('canvas.productFamilyStore'),
        c.get('canvas.item.connector'),
    ));
    container.share('canvas.projectListItemStore', () => new ProjectListItemStore());
    container.share('canvas.itemFactory', (c) => {
        const minWidthValidator = c.get('canvas.item.minWidthValidator');
        const minHeightValidator = c.get('canvas.item.minHeightValidator');
        const minDepthValidator = c.get('canvas.item.minDepthValidator');
        const maxDepthValidator = c.get('canvas.item.maxDepthValidator');
        const factory = new Ofcs.Editor.Item.ItemFactory(
            c.get('canvas.productionDataStore'),
            c.get('canvas.projectListItemStore'),
        );

        // validators
        // ==========

        // validators: circle
        factory.registerItemValidator('circle', minWidthValidator);
        factory.registerItemValidator('circle', minHeightValidator);
        factory.registerItemValidator('circle', minDepthValidator);
        factory.registerItemValidator('circle', maxDepthValidator);

        // validators: rectangle
        factory.registerItemValidator('rectangle', minWidthValidator);
        factory.registerItemValidator('rectangle', minHeightValidator);
        factory.registerItemValidator('rectangle', minDepthValidator);
        factory.registerItemValidator('rectangle', maxDepthValidator);

        // validators: grip
        factory.registerItemValidator('grip', minWidthValidator);
        factory.registerItemValidator('grip', minDepthValidator);
        factory.registerItemValidator('grip', maxDepthValidator);

        // validators: cutsquare
        factory.registerItemValidator('cutsquare', minWidthValidator);
        factory.registerItemValidator('cutsquare', minHeightValidator);

        // validators: drill
        factory.registerItemValidator('drill', minWidthValidator);
        factory.registerItemValidator('drill', minHeightValidator);

        return factory;
    });
    container.share('canvas.item.configBuilder', () => new ChainConfigBuilder([
        new Ofcs.Editor.Item.ConfigBuilder.CenterPositionConfigBuilder(),
        new Ofcs.Editor.Item.ConfigBuilder.TopLeftPositionConfigBuilder(),
    ]));
    container.share('canvas.item.minWidthValidator', c => new Ofcs.Editor.Item.Validator.MinWidthValidator(
        c.get('unit.measurementsConverter'),
        c.get('unit.lengthUnit'),
    ));
    container.share('canvas.item.minHeightValidator', c => new Ofcs.Editor.Item.Validator.MinHeightValidator(
        c.get('unit.measurementsConverter'),
        c.get('unit.lengthUnit'),
    ));
    container.share('canvas.item.minDepthValidator', c => new Ofcs.Editor.Item.Validator.MinDepthValidator(
        c.get('unit.measurementsConverter'),
        c.get('unit.lengthUnit'),
    ));
    container.share('canvas.item.maxDepthValidator', c => new Ofcs.Editor.Item.Validator.MaxDepthValidator(
        c.get('unit.measurementsConverter'),
        c.get('unit.lengthUnit'),
    ));
    container.share('canvas.views', () => new ViewCollection());
    container.share('canvas.selection', () => new SelectionCollection());
    container.share('canvas.polygonCollisionChecker', () => new PolygonCollisionChecker());
    container.share('canvas.treeCollisionChecker', c => new TreeCollisionChecker(
        c.get('canvas.polygonCollisionChecker'),
    ));
    container.share('canvas.itemCollisionChecker', c => new ItemCollisionChecker(
        c.get('canvas.treeCollisionChecker'),
        c.get('canvas.collisionExceptionService'),
    ));
    container.share('canvas.outOfContainerChecker', c => new OutOfContainerChecker(
        c.get('canvas.polygonCollisionChecker'),
    ));
    container.share('canvas.itemOverlappingCollisionChecker', c => new ItemOverlappingCollisionChecker(
        c.get('canvas.treeCollisionChecker'),
    ));
    // TODO: This is deprecated
    container.share('canvas.tabletopCollisionChecker', () => new TabletopCollisionChecker());

    container.share(
        'canvas.collisionExceptionService',
        () => new CollisionExceptionService(),
    );

    container.share('canvas.tools.select', c => new Ofcs.Editor.Svg.Tool.SelectTool(
        c.get('canvas.contextManager'),
        c.get('canvas.selection'),
        c.get('canvas.command.history'),
        c.get('editor.translations'),
        c.get('canvas.command.commandExecutor'),
    ));
    container.share('canvas.tools.move', () => new Ofcs.Editor.Svg.Tool.MoveTool());
    container.share('canvas.tools.text', c => new Ofcs.Editor.Svg.Tool.TextTool(
        c.get('canvas.contextManager'),
        c.get('canvas.selection'),
        c.get('canvas.command.history'),
        c.get('canvas.itemFactory'),
        c.get('editor.translations'),
        c.get('canvas.command.commandExecutor'),
    ));
    container.share('canvas.tools.createRectangle', () => new Ofcs.Editor.Svg.Tool.CreateRectangleTool());
    container.share('canvas.tools.createCutsquare', () => new Ofcs.Editor.Svg.Tool.CreateCutsquareTool());
    container.share('canvas.tools.createCutangle', () => new Ofcs.Editor.Svg.Tool.CreateCutangleTool());
    container.share('canvas.tools.createCircle', () => new Ofcs.Editor.Svg.Tool.CreateCircleTool());
    container.share('canvas.tools.createDrill', () => new Ofcs.Editor.Svg.Tool.CreateDrillTool());
    container.share('canvas.tools.createGrip', () => new Ofcs.Editor.Svg.Tool.CreateGripTool());
    container.share('canvas.tools.createTableleg', () => new Ofcs.Editor.Svg.Tool.CreateTablelegTool());
    container.share('canvas.tools.createConnector', () => new Ofcs.Editor.Svg.Tool.CreateConnectorTool());
    container.share(
        'canvas.tools.createVentilationgrill',
        () => new Ofcs.Editor.Svg.Tool.CreateVentilationgrillTool(),
    );
    container.share('canvas.tools.segmentForeground', () => new Ofcs.Editor.Svg.Tool.SegmentForegroundTool());
    container.share('canvas.tools.segmentBackground', () => new Ofcs.Editor.Svg.Tool.SegmentBackgroundTool());
    container.share('canvas.tools', c => new Ofcs.Editor.Svg.Tool.Tools([
        c.get('canvas.tools.select'),
        c.get('canvas.tools.move'),
        c.get('canvas.tools.text'),
        c.get('canvas.tools.createRectangle'),
        c.get('canvas.tools.createCutsquare'),
        c.get('canvas.tools.createCutangle'),
        c.get('canvas.tools.createCircle'),
        c.get('canvas.tools.createDrill'),
        c.get('canvas.tools.createGrip'),
        c.get('canvas.tools.createTableleg'),
        c.get('canvas.tools.createConnector'),
        c.get('canvas.tools.createVentilationgrill'),
    ]));

    container.share('canvas.svg.guideManagerView', c => new Ofcs.Editor.Svg.GuideManagerView(
        c.get('canvas.guideManager'),
        c.get('canvas.svg.layerManager'),
        c.get('canvas.contextManager'),
        { guideColor: c.get('editor.color.guideColor'), snapColor: c.get('editor.color.snapColor') },
    ));
    container.share('canvas.svg.gridView', c => new Ofcs.Editor.Svg.GridView(
        c.get('canvas.gridManager'),
        c.get('canvas.svg.layerManager'),
        c.get('canvas.svg.pathFactory'),
    ));
    container.share('canvas.svg.extractionMarkerView', c => new Ofcs.Editor.Svg.ExtractionMarkerView(
        c.get('canvas'),
    ));

    container.share(
        'canvas.validator.itemHeightStrategy.fullyRecessed',
        c => new FullyRecessedStrategy(c.get('canvas.bagDepthPadding')),
    );

    container.share(
        'canvas.validator.itemHeightStrategy.itemDepth',
        c => new ItemDepthStrategy(
            c.get('canvas.maxDepth'),
            c.get('canvas.bagDepthPadding'),
        ),
    );

    // TODO: check strategy names
    container.share('canvas.validator.itemHeightStrategy', c => new DelegatingStrategy({
        fullyRecessed: c.get('canvas.validator.itemHeightStrategy.fullyRecessed'),
        optimalDepth: c.get('canvas.validator.itemHeightStrategy.itemDepth'),
    }));

    container.share('canvas.validator', c => new ValidationWatcher(
        c.get('canvas.validator.chain'),
    ));

    container.share('canvas.validator.chain', c => new ChainValidator(
        [
            c.get('canvas.validator.collision'),
            c.get('canvas.validator.outOfContainer'),
            c.get('canvas.validator.itemHeight'),
            c.get('canvas.validator.itemVersion'),
            c.get('canvas.validator.itemSuccessor'),
            c.get('canvas.validator.itemAvailable'),
            c.get('canvas.validator.tabletopChain'),
        ],
    ));

    container.share('canvas.validator.tabletopChain', c => new ChainValidator(
        [
            // The order is important, tabletopCollision validator needs to run before tabletopArea
            c.get('canvas.validator.tabletopCollision'),
            c.get('canvas.validator.tabletopArea'),
        ],
    ));

    container.share('canvas.validator.view', c => new ValidatorView(
        c.get('canvas.validator'),
        [
            c.get('canvas.validator.badgeView'),
            c.get('canvas.validator.collisionView'),
        ],
    ));

    container.share('canvas.validator.badgeView', c => new BadgeView(
        c.get('canvas.overlayManager'),
    ));

    container.share('canvas.validator.collisionView', c => new CollisionView(
        c.get('canvas.itemCollisionChecker'),
        c.get('canvas.svg.layerManager'),
        c.get('canvas.svg.pathFactory'),
        c.get('canvas.selection'),
    ));

    container.share('canvas.validator.collision', c => new CollisionValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('canvas.itemCollisionChecker'),
        c.get('editor.message.collision'),
    ));

    container.share('canvas.validator.outOfContainer', c => new OutOfContainerValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('canvas.outOfContainerChecker'),
        c.get('editor.message.outOfFoam'),
    ));

    container.share('canvas.validator.tabletopCollision', c => new TabletopCollisionValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('canvas.tabletopCollisionChecker'),
        c.get('editor.message.collision'),
    ));

    container.share('canvas.validator.tabletopArea', c => new TabletopAreaValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('editor.message.tabletopArea'),
    ));

    container.share('canvas.validator.itemHeight', c => new ItemHeightValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('canvas.validator.itemHeightStrategy'),
        c.get('editor.message.itemHeight'),
    ));

    container.share('canvas.validator.itemVersion', c => new ItemVersionValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('canvas.productFamilyStore'),
        c.get('editor.message.itemVersion'),
    ));

    container.share('canvas.validator.itemSuccessor', c => new ItemSuccessorValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('editor.message.itemSuccessor'),
    ));

    container.share('canvas.validator.itemAvailable', c => new ItemAvailableValidator(
        c.get('canvas.validator.problemFactory'),
        c.get('editor.message.itemAvailable'),
    ));

    container.share('canvas.validator.problemFactory', c => new ProblemFactory(
        c.get('canvas.itemFactory'),
        c.get('canvas.command.factory'),
        c.get('canvas.command.history'),
        c.get('canvas.selection'),
    ));

    container.share('canvas.itemViewFactory', c => new Ofcs.Editor.Svg.ItemView.ViewFactory(
        c.get('canvas'),
        c.get('canvas.svg.layerManager'),
        c.get('canvas.svg.pathFactory'),
        c.get('canvas.overlayManager'),
        c.get('editor.translations'),
    ));

    container.share('canvas.itemViewManager', c => new Ofcs.Editor.Canvas.ItemView.ViewManager(
        c.get('canvas.views'),
        c.get('canvas.itemViewFactory'),
    ));

    container.share('canvas', c => new SvgCanvas(
        c.get('canvas.layoutId'),
        c.get('canvas.wrapper'),
        c.get('canvas.svg.element'),
        c.get('canvas.svg.wrapper'),
        c.get('canvas.contextManager'),
        c.get('canvas.itemFactory'),
        c.get('canvas.svg.pathFactory'),
        c.get('canvas.command.history'),
        c.get('canvas.svg.layerManager'),
        c.get('canvas.views'),
        c.get('canvas.selection'),
        c.get('canvas.tools'),
        c.get('canvas.command.commandExecutor'),
        c.get('canvas.item.configBuilder'),
        {
            zoomMin: c.get('editor.value.zoomMin'),
            zoomMax: c.get('editor.value.zoomMax'),
            loadingIndicatorTemplate: c.get('editor.template.loadingIndicator'),
            translations: {
                loadingItem: c.get('editor.message.loadingItem'),
            },
        },
    ));

    container.share('canvas.overlayManager', c => new Ofcs.Editor.Canvas.Overlay.Manager(c.get('canvas')));
}
